import { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Wand2, X, CheckCircle, AlertCircle } from 'lucide-react';
import { defaultChannels } from '../../../config/channels';
import type { CommunicationChannel, Post } from '../../../types';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Calendar } from '../../ui/calendar';
import type { ChannelConfig } from '../../../types/generation';
import {
  channelIcons,
  ChannelSvgIcon,
  AGENT_ICONS,
  getAgentDisplayName,
  getChannelDisplayConfig,
  networkToPlatformType,
} from '../../../utils/Constants';
import { useGeneration } from '../../../hooks/useGeneration';
import { getPipelineSteps, AGENT_STEP } from '../../../utils/pipelineSteps';

interface MobileGeneratePanelProps {
  communicationChannels: CommunicationChannel[];
  onGenerate: (newPosts: Post[]) => void;
  onClose: () => void;
}


export function MobileGeneratePanel({ communicationChannels, onGenerate, onClose }: MobileGeneratePanelProps) {
  const [channelConfigs, setChannelConfigs] = useState<Map<string, ChannelConfig>>(new Map());
  const { generatePosts, isGenerating, generationError, progressState, cancelGeneration, resetProgress } = useGeneration();
  const [error, setError] = useState<string | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const { t } = useLanguage();
  const { currentSpace } = useAuth();
  const logRef = useRef<HTMLDivElement>(null);

  // Sync hook error with local error state
  useEffect(() => {
    if (generationError) setError(generationError);
  }, [generationError]);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [progressState.messages]);

  // Initialize channel configs
  useEffect(() => {
    const configs = new Map<string, ChannelConfig>();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    defaultStartDate.setHours(0, 0, 0, 0);

    communicationChannels
      .filter(c => c.enabled !== false)
      .forEach(channel => {
        configs.set(channel.id, {
          id: channel.id,
          selected: true,
          expanded: false,
          postsCount: 5,
          daysBack: 30,
          useCalendar: false,
          selectedDate: defaultStartDate,
          dateRange: { from: defaultStartDate, to: today },
          platform: networkToPlatformType(channel.network),
          positioning: channel.positioning || '',
          imageProvider: (channel.image_provider || 'openjourney') as 'openjourney' | 'gemini-3.1-flash-image',
        });
      });
    setChannelConfigs(configs);
    setError(null);
  }, [communicationChannels]);

  const toggleChannel = (channelId: string) => {
    setChannelConfigs(prev => {
      const next = new Map(prev);
      const config = next.get(channelId);
      if (config) {
        next.set(channelId, {
          ...config,
          selected: !config.selected,
          expanded: config.selected ? false : config.expanded,
        });
      }
      return next;
    });
  };

  const toggleExpand = (channelId: string) => {
    setChannelConfigs(prev => {
      const next = new Map(prev);

      next.forEach((config, id) => {
        if (id !== channelId && config.expanded) {
          next.set(id, { ...config, expanded: false });
        }
      });

      const config = next.get(channelId);
      if (config && config.selected) {
        next.set(channelId, {
          ...config,
          expanded: !config.expanded,
        });
      }
      return next;
    });
  };

  const updateChannelConfig = (channelId: string, updates: Partial<ChannelConfig>) => {
    setChannelConfigs(prev => {
      const next = new Map(prev);
      const config = next.get(channelId);
      if (config) {
        next.set(channelId, { ...config, ...updates });
      }
      return next;
    });
  };

  const handleDateSelect = (channelId: string, date: Date | undefined) => {
    if (date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      updateChannelConfig(channelId, {
        selectedDate: date,
        dateRange: { from: date, to: today },
      });
    } else {
      updateChannelConfig(channelId, {
        selectedDate: undefined,
        dateRange: undefined,
      });
    }
  };

  const handleGenerate = async () => {
    const selectedChannels = Array.from(channelConfigs.values()).filter(c => c.selected);

    if (selectedChannels.length === 0) return;

    setError(null);
    setShowProgress(true);
    resetProgress();

    const firstSelectedChannel = communicationChannels.find(c =>
      selectedChannels.some(sc => sc.id === c.id)
    );
    // V4-FRONT-ANY-USAGE batch 6 2026-05-29 — `language` (singulier) legacy hors type
    const channelLanguage =
      firstSelectedChannel?.languages?.[0] ||
      (firstSelectedChannel as { language?: string } | undefined)?.language ||
      'fr';

    const newPosts = await generatePosts(selectedChannels, { language: channelLanguage });

    if (newPosts.length > 0) {
      onGenerate(newPosts);
      setShowProgress(false);
      onClose();
    } else {
      setTimeout(() => setShowProgress(false), 3000);
    }
  };

  const handleCancel = () => {
    if (isGenerating) cancelGeneration();
    setShowProgress(false);
    onClose();
  };

  const getNetworkConfig = (channelId: string) => {
    const channel = communicationChannels.find(c => c.id === channelId);
    if (!channel) return null;
    return defaultChannels.find(dc => dc.name === channel.network);
  };

  const selectedCount = Array.from(channelConfigs.values()).filter(c => c.selected).length;
  const totalPostsCount = Array.from(channelConfigs.values())
    .filter(c => c.selected)
    .reduce((sum, config) => sum + config.postsCount, 0);

  const pipelineSteps = getPipelineSteps(t);
  const activeStep = AGENT_STEP[progressState.currentAgent ?? ''] ?? -1;

  // GENERATING VIEW
  if (showProgress && isGenerating) {
    return (
      <div className="space-y-4">
        {/* Pipeline — vertical compact */}
        <div
          className="rounded-2xl p-4"
          style={{
            backgroundColor: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
          }}
        >
          <div className="flex flex-col gap-0">
            {pipelineSteps.map((step, i) => {
              const isDone = i < activeStep;
              const isActive = i === activeStep;
              const { Icon, color, label } = step;

              return (
                <div key={i} className="flex items-center gap-3">
                  {/* Left column: circle + vertical line */}
                  <div className="flex flex-col items-center flex-shrink-0" style={{ width: 32 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        border: `2px solid ${color}`,
                        background: isDone || isActive ? color : 'transparent',
                        transform: isActive ? 'scale(1.1)' : 'scale(1)',
                        boxShadow: isActive ? `0 0 0 3px ${color}33` : 'none',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={14} color={isDone || isActive ? 'white' : color} />
                    </div>
                    {/* Vertical connector */}
                    {i < pipelineSteps.length - 1 && (
                      <div
                        style={{
                          width: 2,
                          height: 20,
                          borderRadius: 1,
                          backgroundColor: isDone ? color : 'var(--color-border)',
                          transition: 'background-color 0.3s ease',
                        }}
                      />
                    )}
                  </div>

                  {/* Label */}
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: isActive ? 600 : 400,
                      color: isDone || isActive ? color : 'var(--color-text-secondary)',
                      transition: 'color 0.3s ease',
                      paddingBottom: i < pipelineSteps.length - 1 ? 20 : 0,
                    }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Execution log */}
        <div
          className="rounded-2xl p-4"
          style={{
            backgroundColor: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Exécution
            </span>
            <span
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ backgroundColor: '#3EFFC0' }}
            />
          </div>
          <div
            ref={logRef}
            className="space-y-1 overflow-y-auto"
            style={{ maxHeight: 128 }}
          >
            {progressState.messages.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                Initialisation…
              </p>
            ) : (
              progressState.messages.slice(-15).map((msg, idx) => (
                <div key={idx} className="flex items-start gap-2" style={{ padding: '2px 0' }}>
                  <span className="flex-shrink-0 mt-0.5">
                    {msg.status === 'completed' ? (
                      <CheckCircle className="w-3 h-3" style={{ color: 'var(--color-success)' }} />
                    ) : msg.status === 'error' ? (
                      <AlertCircle className="w-3 h-3" style={{ color: 'var(--color-danger)' }} />
                    ) : (
                      <span style={{ fontSize: 10 }}>{AGENT_ICONS[msg.agent] || '🤖'}</span>
                    )}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                    <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
                      {getAgentDisplayName(msg.agent)}
                    </span>
                    {' '}{msg.message}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Cancel button */}
        <button
          onClick={handleCancel}
          className="w-full py-2 px-3 rounded-full text-sm transition-all flex items-center justify-center gap-2"
          style={{
            backgroundColor: 'var(--color-surface-elevated)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border)',
          }}
        >
          <X className="w-4 h-4" />
          <span>{t.generate.cancel}</span>
        </button>
      </div>
    );
  }

  // IDLE VIEW
  return (
    <div className="space-y-3">
      {/* Error message */}
      {error && (
        <div
          className="p-3 rounded-2xl text-sm border"
          style={{
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            borderColor: 'rgba(255, 0, 0, 0.3)',
            color: 'var(--color-text-primary)',
          }}
        >
          {error}
        </div>
      )}

      {/* Description */}
      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
        {t.generate.description}
      </p>

      {/* Channels list */}
      <div className="space-y-3 max-h-[50vh] overflow-y-auto">
        {communicationChannels
          .filter(c => c.enabled !== false)
          .map((channel) => {
            const config = channelConfigs.get(channel.id);
            if (!config) return null;

            const networkConfig = getNetworkConfig(channel.id);
            if (!networkConfig) return null;

            const displayConfig = getChannelDisplayConfig(channel.network);

            return (
              <div
                key={channel.id}
                className="rounded-xl transition-all"
                style={{
                  border: `1.5px solid ${config.selected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  backgroundColor: config.selected ? 'rgba(85,0,255,0.04)' : 'transparent',
                  opacity: config.selected ? 1 : 0.65,
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Channel header — entire row is clickable to toggle */}
                <div
                  className="flex items-center gap-3 p-3"
                  onClick={() => !isGenerating && toggleChannel(channel.id)}
                  style={{ cursor: isGenerating ? 'not-allowed' : 'pointer' }}
                >
                  {/* Brand accent */}
                  <div
                    className="flex-shrink-0"
                    style={{
                      width: 3,
                      height: 26,
                      borderRadius: 2,
                      backgroundColor: displayConfig?.color || 'var(--color-border)',
                    }}
                  />

                  {/* Icon */}
                  <div className="flex-shrink-0" aria-hidden>
                    <ChannelSvgIcon
                      network={channel.network}
                      size={26}
                      fallbackClassName={`${networkConfig.color} ${networkConfig.textColor}`}
                    />
                  </div>

                  {/* Name */}
                  <span
                    className="flex-1 text-left text-sm font-medium"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {channel.customName || channel.profileName}
                  </span>

                  {/* Posts count pill */}
                  {config.selected && (
                    <span
                      className="rounded-full text-xs font-semibold px-2 py-0.5 flex-shrink-0"
                      style={{
                        backgroundColor: 'rgba(85,0,255,0.1)',
                        color: 'var(--color-primary)',
                      }}
                    >
                      {config.postsCount} posts
                    </span>
                  )}

                  {/* Expand */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleExpand(channel.id); }}
                    disabled={!config.selected || isGenerating}
                    className="p-1 rounded flex-shrink-0"
                    style={{
                      color: config.selected && !isGenerating ? 'var(--color-text-muted)' : 'var(--color-border)',
                      cursor: config.selected && !isGenerating ? 'pointer' : 'not-allowed',
                    }}
                    aria-expanded={config.expanded}
                  >
                    {config.expanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Expanded content */}
                {config.expanded && config.selected && (
                  <div
                    className="px-4 pb-4 space-y-4"
                    style={{ borderTop: '1px solid var(--color-border)', paddingTop: 16 }}
                  >
                    {/* Number of posts */}
                    <div>
                      <Label htmlFor={`posts-${channel.id}`} className="mb-2 block text-xs">
                        {t.generate.postsCount || 'Nombre de posts'}
                      </Label>
                      <Input
                        id={`posts-${channel.id}`}
                        type="number"
                        min="1"
                        max="100"
                        value={config.postsCount}
                        onChange={(e) =>
                          updateChannelConfig(channel.id, {
                            postsCount: parseInt(e.target.value) || 1,
                          })
                        }
                        disabled={isGenerating}
                        className="rounded-full h-9"
                      />
                    </div>

                    {/* Calendar */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs">
                          {t.generate.periodBack || 'Période de veille'}
                        </Label>
                        <button
                          onClick={() => {
                            const defaultStartDate = new Date();
                            defaultStartDate.setDate(defaultStartDate.getDate() - 7);
                            defaultStartDate.setHours(0, 0, 0, 0);
                            handleDateSelect(channel.id, defaultStartDate);
                          }}
                          disabled={isGenerating}
                          className="text-xs px-2 py-1 rounded-full transition-all"
                          style={{
                            color: 'var(--color-primary)',
                            backgroundColor: 'rgba(85, 0, 255, 0.1)',
                          }}
                        >
                          {t.generate.fromToday || 'Depuis auj.'}
                        </button>
                      </div>
                      <div style={{ backgroundColor: 'var(--color-background)' }} className="rounded-2xl p-2">
                        <style>
                          {`
                            .mobile-generate-calendar .rdp {
                              margin: 0;
                              width: 100%;
                            }
                            .mobile-generate-calendar .rdp-day_selected:not([disabled]) {
                              background-color: var(--color-primary) !important;
                              color: var(--color-button-text) !important;
                            }
                            .mobile-generate-calendar .rdp-day_range_middle {
                              background-color: rgba(85, 0, 255, 0.1) !important;
                              color: var(--color-primary) !important;
                            }
                            .mobile-generate-calendar .rdp-day_range_start,
                            .mobile-generate-calendar .rdp-day_range_end {
                              background-color: var(--color-primary) !important;
                              color: var(--color-button-text) !important;
                            }
                          `}
                        </style>
                        <Calendar
                          mode="single"
                          selected={config.selectedDate || undefined}
                          onSelect={(date) => {
                            if (date) handleDateSelect(channel.id, date);
                          }}
                          modifiers={{
                            range_start: config.selectedDate ? [config.selectedDate] : [],
                            range_end: (() => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return [today];
                            })(),
                            range_middle: (date) => {
                              if (!config.selectedDate) return false;
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const compareDate = new Date(date);
                              compareDate.setHours(0, 0, 0, 0);
                              return compareDate > config.selectedDate && compareDate < today;
                            },
                          }}
                          disabled={(date) => date > new Date()}
                          numberOfMonths={1}
                          className="mobile-generate-calendar w-full"
                          classNames={{
                            months: 'flex w-full',
                            month: 'space-y-3 w-full',
                            caption: 'flex justify-center pt-1 relative items-center mb-2',
                            caption_label: 'text-sm font-medium',
                            nav: 'space-x-1 flex items-center',
                            nav_button: 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 rounded transition-opacity',
                            nav_button_previous: 'absolute left-1',
                            nav_button_next: 'absolute right-1',
                            table: 'w-full border-collapse',
                            head_row: 'flex w-full gap-1',
                            head_cell: 'rounded-md flex-1 min-w-0 font-medium text-xs text-center pb-2',
                            row: 'flex w-full gap-1 mb-1',
                            cell: 'relative text-center flex-1 min-w-0',
                            day: 'h-9 w-full p-0 font-normal rounded-lg border transition-colors flex items-center justify-center text-xs',
                            day_selected: 'font-semibold',
                            day_today: 'font-semibold',
                            day_outside: 'opacity-30',
                            day_disabled: 'opacity-30 cursor-not-allowed',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Generate button */}
      <button
        onClick={handleGenerate}
        disabled={selectedCount === 0 || isGenerating}
        className="w-full py-2 px-3 rounded-full text-sm transition-all flex items-center justify-center gap-2"
        style={
          selectedCount > 0 && !isGenerating
            ? {
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-button-text)',
              }
            : {
                backgroundColor: 'var(--color-border)',
                color: 'var(--color-text-muted)',
                cursor: 'not-allowed',
                opacity: 0.5,
              }
        }
      >
        <Wand2 className="w-4 h-4" />
        <span>
          {isGenerating
            ? t.generate.generating
            : `${t.generate.generate}${totalPostsCount > 0 && !isGenerating ? ` ${totalPostsCount} posts` : ''}`}
        </span>
      </button>
    </div>
  );
}
